import React, { useState, useEffect } from 'react';
import { listFiles, listBranches, listCommits } from 'service/ncoreBoardService';
import styles from './CodeTab.module.scss';

const DirIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9D3EF7' }}>
        <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z"/>
    </svg>
);
const CodeFileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: '#60a5fa' }}>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="10" y1="13" x2="8" y2="15"/><line x1="8" y1="15" x2="10" y2="17"/>
        <line x1="14" y1="13" x2="16" y2="15"/><line x1="16" y1="15" x2="14" y2="17"/>
    </svg>
);
const TextFileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: 'rgba(255,255,255,0.5)' }}>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
);
const ImageFileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: '#f472b6' }}>
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
    </svg>
);
const ConfigFileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: '#fbbf24' }}>
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M12 2v2M12 20v2M2 12h2M20 12h2"/>
    </svg>
);

const CODE_EXTS = ['js','jsx','ts','tsx','py','go','rs','java','php','rb','c','cpp','cs','swift','kt','vue','svelte','sh','bash'];
const IMG_EXTS  = ['png','jpg','jpeg','gif','svg','webp','ico','bmp'];
const CFG_EXTS  = ['json','yml','yaml','toml','env','ini','cfg','xml','lock'];

function FileIcon({ file }) {
    if (file.file_type === 'dir') return <DirIcon />;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (CODE_EXTS.includes(ext)) return <CodeFileIcon />;
    if (IMG_EXTS.includes(ext))  return <ImageFileIcon />;
    if (CFG_EXTS.includes(ext))  return <ConfigFileIcon />;
    return <TextFileIcon />;
}

function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function CopyCmd({ cmd, copyText }) {
    const [copied, setCopied] = useState(false);
    function handle() {
        copyText(cmd);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }
    return (
        <div className={styles.setupCmd} onClick={handle} title="Нажмите чтобы скопировать">
            <code className={styles.setupCmdText}>{cmd}</code>
            <span className={styles.setupCmdCopy}>
                {copied ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#10b981' }}>
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                    </svg>
                )}
            </span>
        </div>
    );
}

function EmptyRepo({ repo, repoHttpsUrl, repoSshUrl, currentBranch, copyText }) {
    const [copiedUrl, setCopiedUrl] = useState(false);
    function copyUrl(url) {
        copyText(url);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    }
    return (
        <div className={styles.emptyRepo}>
            <div className={styles.emptyIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: 'rgba(157,62,247,0.5)' }}>
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                    <line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>
                </svg>
            </div>
            <h3 className={styles.emptyTitle}>Репозиторий пустой</h3>
            <p className={styles.emptyDesc}>Начни работу — создай первый коммит</p>

            {/* URL box */}
            <div className={styles.repoUrlBox}>
                <div className={styles.repoUrlLabel}>URL репозитория</div>
                <div className={styles.repoUrlRow}>
                    <code className={styles.repoUrlText}>{repoHttpsUrl}</code>
                    <button className={styles.repoUrlCopy} onClick={() => copyUrl(repoHttpsUrl)} title="Скопировать URL">
                        {copiedUrl ? (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#10b981' }}>
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                        ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <div className={styles.setupBlocks}>
                {/* New repo from scratch */}
                <div className={styles.setupBlock}>
                    <div className={styles.setupBlockTitle}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9D3EF7' }}>
                            <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm5 11h-4v4h-2v-4H7v-2h6V7h2v4h4v2z"/>
                        </svg>
                        Создать новый репозиторий в командной строке
                    </div>
                    <div className={styles.setupCmds}>
                        {[
                            'echo "# ' + repo.name + '" > README.md',
                            'ncore init',
                            'ncore add README.md',
                            `ncore commit -m "first commit"`,
                            `ncore branch -M ${currentBranch}`,
                            `ncore remote add origin ${repoHttpsUrl}`,
                            `ncore push -u origin ${currentBranch}`,
                        ].map((cmd, i) => <CopyCmd key={i} cmd={cmd} copyText={copyText} />)}
                    </div>
                </div>

                {/* Push existing repo */}
                <div className={styles.setupBlock}>
                    <div className={styles.setupBlockTitle}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#9D3EF7' }}>
                            <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                            <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
                        </svg>
                        Подключить существующий репозиторий
                    </div>
                    <div className={styles.setupCmds}>
                        {[
                            `ncore remote add origin ${repoHttpsUrl}`,
                            `ncore branch -M ${currentBranch}`,
                            `ncore push -u origin ${currentBranch}`,
                        ].map((cmd, i) => <CopyCmd key={i} cmd={cmd} copyText={copyText} />)}
                    </div>
                </div>
            </div>
        </div>
    );
}

function timeAgo(dateStr) {
    if (!dateStr) return '';
    // Handle Django's DD.MM.YYYY HH:MM format
    let d = new Date(dateStr);
    if (isNaN(d.getTime())) {
        const m = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/);
        if (m) d = new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5]);
    }
    if (isNaN(d.getTime())) return dateStr;
    const diff = Date.now() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'только что';
    if (minutes < 60) return `${minutes} мин. назад`;
    const h = Math.floor(minutes / 60);
    if (h < 24) return `${h} ч. назад`;
    const days = Math.floor(h / 24);
    if (days === 1) return 'вчера';
    if (days < 30) return `${days} дн. назад`;
    return d.toLocaleDateString('ru-RU');
}

export default function CodeTab({ repo }) {
    const [files, setFiles] = useState([]);
    const [branches, setBranches] = useState([]);
    const [commits, setCommits] = useState([]);
    const [commitsTotal, setCommitsTotal] = useState(0);
    const [showAllCommits, setShowAllCommits] = useState(false);
    const [currentBranch, setCurrentBranch] = useState(repo.default_branch || 'main');
    const [currentPath, setCurrentPath] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [branchOpen, setBranchOpen] = useState(false);
    const [cloneOpen, setCloneOpen] = useState(false);

    useEffect(() => {
        loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [repo.id]);

    useEffect(() => {
        loadFiles();
        loadCommits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [repo.id, currentBranch]);

    useEffect(() => {
        if (currentBranch) setCurrentPath('');
    }, [currentBranch]);

    async function loadBranches() {
        try {
            const data = await listBranches(repo.id);
            setBranches(data.results || data);
        } catch {}
    }

    async function loadFiles() {
        setLoading(true);
        setSelectedFile(null);
        try {
            const data = await listFiles(repo.id, { branch: currentBranch, path: currentPath });
            const results = data.results || data;
            // Sort: dirs first, then files
            results.sort((a, b) => {
                if (a.file_type === b.file_type) return a.name.localeCompare(b.name);
                return a.file_type === 'dir' ? -1 : 1;
            });
            setFiles(results);
        } catch { setFiles([]); }
        setLoading(false);
    }

    async function loadCommits() {
        try {
            const data = await listCommits(repo.id, { branch: currentBranch, page_size: 10 });
            const results = data.results || data;
            setCommits(results);
            setCommitsTotal(data.count || results.length);
        } catch { setCommits([]); setCommitsTotal(0); }
    }

    async function loadAllCommits() {
        try {
            const data = await listCommits(repo.id, { branch: currentBranch, page_size: 200 });
            const results = data.results || data;
            setCommits(results);
            setCommitsTotal(data.count || results.length);
            setShowAllCommits(true);
        } catch {}
    }

    function navigate(file) {
        if (file.file_type === 'dir') {
            setCurrentPath(file.path);
            setBreadcrumbs(prev => [...prev, { name: file.name, path: file.path }]);
        } else {
            setSelectedFile(file);
        }
    }

    function goToBreadcrumb(index) {
        if (index === -1) {
            setCurrentPath('');
            setBreadcrumbs([]);
        } else {
            const crumb = breadcrumbs[index];
            setCurrentPath(crumb.path);
            setBreadcrumbs(prev => prev.slice(0, index + 1));
        }
        setSelectedFile(null);
    }

    const repoHttpsUrl = `https://security.ncore.kg/ncoreboard/${repo.owner?.username}/${repo.slug}`;
    const repoSshUrl   = `git@security.ncore.kg:${repo.owner?.username}/${repo.slug}`;
    const cloneUrl = `ncore clone ${repoHttpsUrl}`;
    const sshUrl   = `ncore clone ${repoSshUrl}`;

    function copyText(text) {
        navigator.clipboard.writeText(text).catch(() => {});
    }

    return (
        <div className={styles.wrap}>
            {/* Toolbar */}
            <div className={styles.toolbar}>
                {/* Branch selector */}
                <div className={styles.branchSelector}>
                    <button className={styles.branchBtn} onClick={() => setBranchOpen(!branchOpen)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
                            <path d="M18 9a9 9 0 01-9 9"/>
                        </svg>
                        {currentBranch}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                        </svg>
                    </button>
                    {branchOpen && (
                        <div className={styles.branchDropdown}>
                            <div className={styles.branchDropdownHeader}>Переключить ветку</div>
                            {branches.map(b => (
                                <div
                                    key={b.id}
                                    className={`${styles.branchOption} ${b.name === currentBranch ? styles.branchActive : ''}`}
                                    onClick={() => { setCurrentBranch(b.name); setBranchOpen(false); setCurrentPath(''); setBreadcrumbs([]); }}
                                >
                                    {b.is_default && <span className={styles.defaultTag}>default</span>}
                                    {b.is_protected && <span className={styles.protectedTag}>🔒</span>}
                                    {b.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Breadcrumbs */}
                <div className={styles.breadcrumbs}>
                    <span className={styles.crumb} onClick={() => goToBreadcrumb(-1)}>{repo.name}</span>
                    {breadcrumbs.map((c, i) => (
                        <React.Fragment key={i}>
                            <span className={styles.crumbSep}>/</span>
                            <span className={styles.crumb} onClick={() => goToBreadcrumb(i)}>{c.name}</span>
                        </React.Fragment>
                    ))}
                    {selectedFile && (
                        <>
                            <span className={styles.crumbSep}>/</span>
                            <span className={styles.crumbActive}>{selectedFile.name}</span>
                        </>
                    )}
                </div>

                {/* Clone button */}
                <div className={styles.cloneWrapper}>
                    <button className={styles.cloneBtn} onClick={() => setCloneOpen(!cloneOpen)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                            <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                        Клонировать
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                    </button>
                    {cloneOpen && (
                        <div className={styles.cloneDropdown}>
                            <div className={styles.cloneSection}>
                                <div className={styles.cloneSectionTitle}>HTTPS</div>
                                <div className={styles.cloneRow}>
                                    <code className={styles.cloneCode}>{cloneUrl}</code>
                                    <button className={styles.copyBtn} onClick={() => copyText(cloneUrl)} title="Скопировать">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className={styles.cloneSection}>
                                <div className={styles.cloneSectionTitle}>SSH</div>
                                <div className={styles.cloneRow}>
                                    <code className={styles.cloneCode}>{sshUrl}</code>
                                    <button className={styles.copyBtn} onClick={() => copyText(sshUrl)} title="Скопировать">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className={styles.cloneCommands}>
                                <div className={styles.cloneSectionTitle}>Команды NcoreBoard</div>
                                <code>ncore init</code>
                                <code>ncore status</code>
                                <code>ncore add .</code>
                                <code>ncore commit -m "message"</code>
                                <code>ncore push origin {currentBranch}</code>
                                <code>ncore pull origin {currentBranch}</code>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* File view */}
            {selectedFile ? (
                <div className={styles.fileView}>
                    <div className={styles.fileViewHeader}>
                        <button className={styles.backBtn} onClick={() => setSelectedFile(null)}>
                            ← Назад
                        </button>
                        <span className={styles.fileName}>{selectedFile.name}</span>
                        <span className={styles.fileSize}>{formatSize(selectedFile.size)}</span>
                        <span className={styles.fileSha}>{selectedFile.sha?.slice(0, 7)}</span>
                    </div>
                    {selectedFile.last_commit && (
                        <div className={styles.fileCommitInfo}>
                            <span className={styles.commitSha}>{selectedFile.last_commit.sha?.slice(0, 7)}</span>
                            <span className={styles.commitMsg}>{selectedFile.last_commit.message}</span>
                            <span className={styles.commitAuthor}>{selectedFile.last_commit.author_name}</span>
                            <span className={styles.commitDate}>{timeAgo(selectedFile.last_commit.committed_at)}</span>
                        </div>
                    )}
                    <pre className={styles.fileContent}>
                        <code>{selectedFile.content || '(файл пустой)'}</code>
                    </pre>
                </div>
            ) : (
                <div className={styles.fileTree}>
                    {loading ? (
                        <div className={styles.loading}>Загрузка...</div>
                    ) : files.length === 0 ? (
                        <EmptyRepo repo={repo} repoHttpsUrl={repoHttpsUrl} repoSshUrl={repoSshUrl} currentBranch={currentBranch} copyText={copyText} />
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Имя</th>
                                    <th>Последний коммит</th>
                                    <th>Дата</th>
                                    <th>Размер</th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map(file => (
                                    <tr key={file.id} className={styles.row} onClick={() => navigate(file)}>
                                        <td className={styles.nameCell}>
                                            <FileIcon file={file} />
                                            <span className={styles.fileNameCol}>{file.name}</span>
                                        </td>
                                        <td className={styles.commitCell}>
                                            {file.last_commit?.message?.slice(0, 60) || '—'}
                                        </td>
                                        <td className={styles.dateCell}>
                                            {timeAgo(file.last_commit?.committed_at || file.updated_at)}
                                        </td>
                                        <td className={styles.sizeCell}>
                                            {file.file_type === 'dir' ? '—' : formatSize(file.size)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Commits history ───────────────────────────────── */}
            {commits.length > 0 && (
                <div className={styles.commitsSection}>
                    <div className={styles.commitsSectionHeader}>
                        <span className={styles.commitsSectionTitle}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="4"/><line x1="1.05" y1="12" x2="7" y2="12"/>
                                <line x1="17.01" y1="12" x2="22.96" y2="12"/>
                            </svg>
                            История коммитов
                            <span className={styles.commitsCountBadge}>{commitsTotal}</span>
                        </span>
                        {!showAllCommits && commitsTotal > 10 && (
                            <button className={styles.showAllBtn} onClick={loadAllCommits}>
                                Показать все {commitsTotal}
                            </button>
                        )}
                    </div>
                    <div className={styles.commitsList}>
                        {commits.map(c => (
                            <div key={c.id} className={styles.commitRow}>
                                <div className={styles.commitLeft}>
                                    <div className={styles.commitAvatar}>
                                        {c.author?.avatar
                                            ? <img src={c.author.avatar} alt={c.author.username} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                            : <div style={{
                                                width: 28, height: 28, borderRadius: '50%',
                                                background: 'linear-gradient(135deg,#9D3EF7,#7c3aed)',
                                                color: '#fff', display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', fontWeight: 700, fontSize: 11,
                                              }}>
                                                {(c.author?.username || c.author_name || '?')[0].toUpperCase()}
                                              </div>
                                        }
                                    </div>
                                    <div className={styles.commitInfo}>
                                        <span className={styles.commitMsg}>{c.message}</span>
                                        <span className={styles.commitMeta}>
                                            <span className={styles.commitAuthorName}>{c.author?.username || c.author_name}</span>
                                            {c.additions > 0 && <span className={styles.commitAdditions}>+{c.additions}</span>}
                                            {c.deletions > 0 && <span className={styles.commitDeletions}>-{c.deletions}</span>}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.commitRight}>
                                    <span className={styles.commitShaTag}>{c.sha?.slice(0, 7)}</span>
                                    <span className={styles.commitDate}>{timeAgo(c.committed_at)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
